import React, { useState, useMemo } from "react";
import styled from "@emotion/styled";
import BasicButton from "@components/button/BasicButton";
import BackHeader from "@components/header/BackHeader";
import CustomCheckbox from "@components/input/CustomCheckbox";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { queryClient } from "@libs/reactQuery/queryClient";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import QuantityControl from "@components/input/QuantityControl";
import CustomTooltip from "@components/tooltip/CustomTooltip";
import Image from "@components/item/Image";
import useOrderStore from "@store/order/useOrderStore";
import {
  calculateProductPrice,
  calculateShippingThreshold,
} from "@utils/price";
import { useTranslation } from "react-i18next";
import { toPrice } from "@utils/functions";
import { Flex } from "antd";

const Cart = () => {
  const { t } = useTranslation();

  // 1. API 관련 설정
  const {
    getCart,
    deleteCart,
    getMemberSubscription,
    patchCartAmount,
    getDeliveryList,
  } = useApiRequest();

  const { data: deliveryList } = useQuery({
    queryKey: ["deliveryList"],
    queryFn: () => getDeliveryList(),
  });

  const { data: cartList } = useQuery({
    queryKey: ["cartList"],
    queryFn: getCart,
  });
  const { data: subscribeDetail } = useQuery({
    queryKey: ["subscribeDetail"],
    queryFn: getMemberSubscription,
  });

  // 2. 상태 관리
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [maxQuantityModalOpen, setMaxQuantityModalOpen] = useState(false);
  const isKoreanShipping = deliveryList?.shipping_country === "KR";

  // 3. 기타 설정
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("language");
  const { setOrder, setOrderType, setCartIds } = useOrderStore();

  // 4. Mutations
  const { mutate: deleteMutate } = useMutation({
    mutationFn: deleteCart,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cartList"] }),
  });

  const { mutate: updateAmount } = useMutation({
    mutationFn: ({ id, amount }: { id: number; amount: number }) =>
      patchCartAmount({ id, amount }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cartList"] }),
  });

  // 5. 핸들러 함수들
  const handleCheck = (cartId?: number, isAllCheck?: boolean) => {
    if (isAllCheck !== undefined) {
      const allCartIds =
        cartList?.flatMap((seller: any) =>
          seller.product_list.map((product: any) => product.cart_id),
        ) || [];
      setCheckedItems(isAllCheck ? allCartIds : []);
      return;
    }

    if (cartId !== undefined) {
      setCheckedItems((prev) =>
        prev.includes(cartId)
          ? prev.filter((id) => id !== cartId)
          : [...prev, cartId],
      );
    }
  };

  const handleQuantityChange = (cartId: number, newQuantity: number) => {
    updateAmount({ id: cartId, amount: newQuantity });
  };

  const handleDeleteSelected = () => {
    if (checkedItems.length) setDeleteModalOpen(true);
  };

  // 선택한 상품 셀러별로 그룹화
  const selectedItemsBySeller = cartList?.reduce((acc: any, seller: any) => {
    const selectedProducts = seller.product_list.filter((item: any) =>
      checkedItems.includes(item.cart_id),
    );

    if (selectedProducts.length > 0) {
      acc.push({
        seller_id: seller.seller_id,
        nickname: seller.nickname,
        profile_image: seller.profile_image,
        free_shipping_fee: seller.free_shipping_fee,
        shipping_fee: seller.shipping_fee,
        product_list: selectedProducts,
      });
    }

    return acc;
  }, []);

  const handlePurchase = () => {
    setOrder(selectedItemsBySeller);
    setCartIds(checkedItems);
    setOrderType("CART");
    navigate("/order");
  };

  // 6. 전체 상품 개수
  const totalProducts =
    cartList?.reduce(
      (sum: number, seller: any) => sum + seller.product_list.length,
      0,
    ) || 0;

  // 7. 가격 계산 로직
  const calculations = useMemo(() => {
    if (!cartList)
      return {
        sellerTotals: {},
        totalShipping: 0,
        totalAmount: 0,
        totalDiscount: 0,
        totalPaymentAmount: 0,
      };

    const sellerTotals = cartList.reduce((acc: any, seller: any) => {
      let OriginalProductTotal = 0;
      let ProductTotal = 0;
      let PaymentProductTotal = 0;

      seller.product_list.forEach((product: any) => {
        if (!checkedItems.includes(product.cart_id)) return;

        OriginalProductTotal += product.price * product.amount;
        ProductTotal += product.price * 2 * product.amount;
        PaymentProductTotal +=
          calculateProductPrice(
            product.price,
            subscribeDetail?.is_subscribed,
            product.product_condition === "NEW",
          ) * product.amount;
      });

      const freeShippingThreshold = calculateShippingThreshold(
        seller.free_shipping_fee,
        subscribeDetail?.is_subscribed,
        isKoreanShipping,
      );

      const shippingFee =
        PaymentProductTotal === 0
          ? 0
          : PaymentProductTotal >= freeShippingThreshold
            ? 0
            : seller.shipping_fee;

      return {
        ...acc,
        [seller.seller_id]: {
          ProductTotal,
          OriginalProductTotal,
          PaymentProductTotal,
          shippingFee,
        },
      };
    }, {});

    const totalShipping = Object.values(sellerTotals).reduce(
      (sum: number, { shippingFee }: any) => sum + shippingFee,
      0,
    );

    const totalAmount = Object.values(sellerTotals).reduce(
      (sum: number, { ProductTotal }: any) => sum + ProductTotal,
      0,
    );

    const totalPaymentAmount = Object.values(sellerTotals).reduce(
      (sum: number, { PaymentProductTotal }: any) => sum + PaymentProductTotal,
      0,
    );

    const totalDiscount = Object.values(sellerTotals).reduce(
      (sum: number, { ProductTotal, PaymentProductTotal }: any) =>
        sum + ProductTotal - PaymentProductTotal,
      0,
    );

    return {
      sellerTotals,
      totalShipping,
      totalAmount,
      totalDiscount,
      totalPaymentAmount,
    };
  }, [cartList, checkedItems, subscribeDetail?.is_subscribed]);

  return (
    <>
      <BackHeader title="장바구니" />

      <CartContainer>
        {/* 전체 선택 및 삭제 헤더 */}
        <CartHeader>
          <CustomCheckbox
            checked={totalProducts === checkedItems.length}
            onChange={(checked) => handleCheck(undefined, checked)}
          />
          <DeleteButton>
            <div>{t("전체선택")}</div>
            <button onClick={handleDeleteSelected}>{t("선택삭제")}</button>
          </DeleteButton>
        </CartHeader>

        {/* 상품 목록 */}
        {cartList?.map((seller: any) => (
          <div key={seller.seller_id}>
            {/* 셀러 프로필 */}
            <UserProfileContainer>
              <UserImage
                src={seller?.profile_image || theme.image.default_user.default}
                alt={seller.nickname}
              />
              <UserName>{seller.nickname}</UserName>
            </UserProfileContainer>

            {/* 상품 목록 */}
            {seller.product_list.map((item: any) => (
              <CartItemContainer key={item.cart_id}>
                <ItemContainer>
                  <ItemContent>
                    <CheckboxWrapper>
                      <CustomCheckbox
                        checked={checkedItems.includes(item.cart_id)}
                        onChange={() => handleCheck(item.cart_id)}
                      />
                    </CheckboxWrapper>

                    <ItemImageWrapper>
                      <ItemImage src={item.thumbnail_url} alt="image" />
                    </ItemImageWrapper>

                    <ItemDetails>
                      <BrandName>
                        {language === "ko"
                          ? item.brand_name_ko
                          : item.brand_name_en}
                      </BrandName>
                      <ItemName>
                        {language === "ko"
                          ? item.product_name_ko
                          : item.product_name_en}
                      </ItemName>
                      <ItemOption>
                        {t("옵션")} : {item.color_name} / {item.size_name}
                      </ItemOption>
                    </ItemDetails>
                  </ItemContent>

                  <PriceQuantityWrapper>
                    <QuantityBox>
                      <TitleGray>{t("수량")}</TitleGray>
                      <QuantityControl
                        value={item.amount}
                        onChange={(newQuantity) =>
                          handleQuantityChange(item.cart_id, newQuantity)
                        }
                        min={1}
                        max={item.stock}
                        onMaxQuantity={() => setMaxQuantityModalOpen(true)}
                      />
                    </QuantityBox>

                    <PriceBox>
                      <TitleGray>{t("상품 금액")}</TitleGray>
                      <ItemPrice>
                        {toPrice(item.price * 2 * item.amount)}
                      </ItemPrice>
                    </PriceBox>
                  </PriceQuantityWrapper>
                </ItemContainer>
              </CartItemContainer>
            ))}

            {/* 셀러별 합계 정보 */}
            <SellerSummary>
              <SummaryRow>
                <SummaryLabel>
                  <div>{t("배송비")}</div>
                  <CustomTooltip
                    content={
                      <Flex vertical>
                        <span>
                          {t(
                            "* 도서산간 지역의 기준은 판매자가 이용하는 택배사마다 상이할 수 있습니다.",
                          )}
                        </span>
                        <span>{t("도서산간 서브")}</span>
                      </Flex>
                    }>
                    <TooltipBox>
                      <Image
                        src={theme.image.warning_circle.default}
                        alt="image"
                        width="1.6rem"
                        height="1.6rem"
                      />
                    </TooltipBox>
                  </CustomTooltip>
                </SummaryLabel>
                <SummaryValue>
                  {toPrice(
                    calculations?.sellerTotals[seller.seller_id]?.shippingFee,
                  )}
                </SummaryValue>
              </SummaryRow>

              <SummaryRow>
                <SummaryLabel>{t("상품 금액")}</SummaryLabel>
                <SummaryValue>
                  {toPrice(
                    calculations?.sellerTotals[seller.seller_id]?.ProductTotal,
                  )}
                </SummaryValue>
              </SummaryRow>
            </SellerSummary>
            <Line />
          </div>
        ))}

        {/* 전체 합계 정보 */}
        <PriceDetails>
          <PriceRow>
            <span>{t("총 배송비")}</span>
            <span>{toPrice(calculations.totalShipping)}</span>
          </PriceRow>

          <PriceRow>
            <span>{t("총 상품 금액")}</span>
            <Price>{toPrice(calculations.totalAmount)}</Price>
          </PriceRow>
        </PriceDetails>
      </CartContainer>

      {/* 구독자이고 할인받은 상품이 있는 경우 */}
      {subscribeDetail?.is_subscribed && calculations?.totalDiscount > 0 ? (
        <PriceRow>
          <DiscountPrice>
            <span>{t("구독 멤버할인 50%")}</span>
            <span>- {toPrice(calculations.totalDiscount)}</span>
          </DiscountPrice>
        </PriceRow>
      ) : null}

      {/* 하단 구매하기 버튼 */}
      <TotalPriceContainer>
        <TotalPrice>
          <span>{t("총 상품 합계")}</span>
          <Price>
            {toPrice(
              calculations.totalPaymentAmount + calculations.totalShipping,
            )}
          </Price>
        </TotalPrice>

        <BasicButton
          title="구매하기"
          width="100%"
          height="5rem"
          onClick={handlePurchase}
          disabled={checkedItems.length === 0}
        />
      </TotalPriceContainer>

      {/* 모달들 */}
      <BasicModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        activeEvent={() => {
          deleteMutate(checkedItems);
          setDeleteModalOpen(false);
        }}
        activeName="확인"
        closeEvent={() => setDeleteModalOpen(false)}
        closeName="취소">
        <ModalContent
          mainMessage="선택한 상품을"
          subMessage="장바구니에서 삭제하시겠습니까?"
        />
      </BasicModal>

      <BasicModal
        open={maxQuantityModalOpen}
        setOpen={setMaxQuantityModalOpen}
        activeEvent={() => setMaxQuantityModalOpen(false)}
        activeName="확인"
        notice>
        <ModalContent mainMessage="최대 수량을 초과할 수 없습니다." />
      </BasicModal>
    </>
  );
};

export default Cart;

const CartContainer = styled.div(({ theme }) => ({
  padding: "2rem",
  backgroundColor: theme.color.white,
}));

const CartHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "1.4rem",
  marginTop: "2rem",
  paddingBottom: "2rem",
  borderBottom: `4px solid ${theme.color.black}`,
}));

const DeleteButton = styled.div(() => ({
  border: "none",
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  marginLeft: "1rem",
}));

const UserProfileContainer = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  margin: "2rem 0",
}));

const UserImage = styled.img(({ theme }) => ({
  width: "4rem",
  height: "4rem",
  borderRadius: "50%",
  objectFit: "cover",
}));

const UserName = styled.div(() => ({
  fontSize: "1.4rem",
  fontWeight: 500,
}));

const PriceDetails = styled.div(({ theme }) => ({
  backgroundColor: theme.color.light_mint,
  padding: "2rem",
  gap: "2rem",
  display: "flex",
  flexDirection: "column",
}));

const PriceRow = styled.div(() => ({
  display: "flex",
  justifyContent: "space-between",
  fontSize: "1.6rem",
}));

const DiscountPrice = styled.div(({ theme }) => ({
  display: "flex",
  width: "100%",
  height: "5rem",
  alignItems: "center",
  padding: "1.5rem 2rem",
  justifyContent: "space-between",
  fontSize: "1.6rem",
  backgroundColor: theme.color.mint,
}));

const TotalPriceContainer = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  padding: "2rem 2rem 4rem 2rem",
}));

const TotalPrice = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "2rem",
  fontSize: "1.6rem",

  "> span": {
    color: theme.color.light_gray,
  },
}));

const Price = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeight.bold,
}));

const CartItemContainer = styled.div(({ theme }) => ({
  display: "flex",
  padding: "2rem 0",
  borderBottom: `1px solid ${theme.color.border_gray}`,
}));

const CheckboxWrapper = styled.div({});

const ItemContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const ItemContent = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  marginBottom: "1.8rem",
  width: "100%",
});

const ItemImageWrapper = styled.div({
  width: "6rem",
  height: "6rem",
});

const ItemImage = styled.img({
  width: "6rem",
  height: "6rem",
  objectFit: "cover",
});

const ItemDetails = styled.div({
  height: "6rem",
  gap: "0.8rem",
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const BrandName = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",

  ">div": {
    fontWeight: theme.fontWeight.regular,
    fontSize: "1.2rem",
  },
}));

const ItemName = styled.div({
  fontSize: "1.2rem",
});

const ItemOption = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  marginBottom: "1rem",
  color: theme.color.gray,
}));

const PriceQuantityWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "auto",
  gap: "2rem",
});

const QuantityBox = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const PriceBox = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
}));

const ItemPrice = styled.div(() => ({
  fontSize: "1.6rem",
}));

export const TitleGray = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.6rem",
}));

// 셀러

const SellerSummary = styled.div(({ theme }) => ({
  padding: "2rem 0",
  gap: "2rem",
  display: "flex",
  flexDirection: "column",
  borderBottom: `4px solid ${theme.color.black}`,
  marginBottom: "2rem",

  "&:last-child": {
    borderBottom: "none",
  },
}));

const SummaryRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

export const SummaryLabel = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.6rem",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
}));

export const SummaryValue = styled.div<{ green?: boolean }>`
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${({ theme, green }) =>
    green ? theme.color.green : theme.color.black};
  font-weight: ${({ green }) => (green ? "700" : "400")};
`;

const FreeShipping = styled.span(() => ({
  fontSize: "1.6rem",
}));

const TooltipBox = styled.div(({ theme }) => ({
  cursor: "pointer",
  marginTop: "0.2rem",
}));

const Line = styled.span(({ theme }) => ({
  width: "100%",
  height: "1px",
  borderBottom: `1px solid ${theme.color.border_gray}`,

  "&:last-child": {
    borderBottom: "none",
  },
}));

const Amount = styled.div(({ theme }) => ({
  color: theme.color.green,
  fontSize: "1.2rem",
  fontWeight: theme.fontWeight.medium,
  marginBottom: "1rem",
}));
